/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/schemas/test-ir.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed                        |
 * |---|------------------------|-------------------|-----------------|-------------------------------------|
 * | 1 | 2026-06-16 (Phase 1)   | Claude Sonnet 4.6 | IR Step 1       | Created — TestIR top-level schema   |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/index.ts           (re-exported as @mimiq/ir)
 * - packages/ir/src/validate.ts        (validateIR uses TestIRSchema)
 * - packages/cli/src/index.ts          (loads IR files, validates before run)
 * - apps/control-plane/src/**          (stores TestIR as Postgres jsonb)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * TargetSchema          — "android" | "ios" | "web"
 * AppConfigSchema       — packageName / bundleId / url
 * DataSourceSchema      — csv | json | inline row variants
 * InterruptionPolicySchema — permissions + crashOnAnr
 * HealingPolicySchema   — maxTierUsed + minConfidence + mode
 * TimeoutsSchema        — step timeout + suite timeout
 * ConfigSchema          — full config block
 * MetaSchema            — id (ULID) + name + tags + owner + createdBy
 * FlowSchema            — id + name + entry? + steps[]
 * HooksSchema           — beforeEach + afterEach
 * TestIRSchema          — canonical top-level IR schema (irVersion "1.0")
 * TestIR                — TypeScript type (inferred)
 * Flow                  — TypeScript type (inferred)
 * HealingPolicy         — TypeScript type (inferred)
 *
 * CALLER → CALLEE MAP
 * --------------------
 * validate.ts → TestIRSchema.safeParse()
 * index.ts    → all exports
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. At least one Flow must have `entry: true` — enforced by a `.refine()` on
 *    TestIRSchema. The runner starts execution from the first entry flow.
 * 2. config.env values are string templates that may contain ${env:KEY} or
 *    ${vault:KEY} references. The interpolate() function resolves them at run time.
 * 3. Healing.mode = "suggest" means the runner emits a HealEvent but does NOT act;
 *    mode = "auto" means the runner acts on Tier-1 results and marks healed.
 * 4. dataSources keys are referenced by forEachRow.dataSource. Unknown keys are
 *    allowed (future data source types round-trip via z.unknown in DataSourceSchema).
 * 5. irVersion is a z.literal("1.0") — any future breaking change bumps this and
 *    requires a migration (packages/ir/src/migrate/).
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
import { z } from "zod";
import { IRStepSchema } from "./steps.js";
// ── Target platforms ─────────────────────────────────────────────────────────
export const TargetSchema = z.enum(["android", "ios", "web"]);
// ── App config ───────────────────────────────────────────────────────────────
export const AppConfigSchema = z.object({
    /** Android package name (e.g. "com.example.app"). */
    packageName: z.string().optional(),
    /** iOS bundle ID (e.g. "com.example.app"). */
    bundleId: z.string().optional(),
    /** Web start URL. */
    url: z.string().optional(),
});
// ── Data sources ─────────────────────────────────────────────────────────────
export const DataSourceSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("csv"),
        /** Path relative to the IR file or absolute. Supports ${env:} references. */
        path: z.string(),
    }),
    z.object({
        type: z.literal("json"),
        path: z.string(),
    }),
    z.object({
        type: z.literal("inline"),
        rows: z.array(z.record(z.unknown())),
    }),
]);
// ── Interruption policy ──────────────────────────────────────────────────────
export const InterruptionPolicySchema = z
    .object({
    /** How to handle Android permission dialogs. Default: allowAll (tap the allow button). */
    permissions: z.enum(["allowAll", "denyAll", "prompt"]).default("allowAll"),
    /** Fail the step on ANR (not-responding) dialog. Default true. */
    crashOnAnr: z.boolean().default(true),
})
    .default({});
// ── Healing policy ───────────────────────────────────────────────────────────
export const HealingPolicySchema = z
    .object({
    /**
     * Maximum AI tier the runner may use. 0 = deterministic only; 1 = ONNX (default);
     * 2 = Ollama (local VLM); 3 = remote LLM (opt-in with budget).
     */
    maxTierUsed: z.number().int().min(0).max(3).default(1),
    /** Accept Tier-1 result only if top-1 score >= this threshold. Default 0.75. */
    minConfidence: z.number().min(0).max(1).default(0.75),
    /**
     * auto   — Tier-1 accepted result acts on device; step marked "passed (healed)".
     * suggest — Tier-1 result emits HealEvent but runner FAILS; human must commit.
     */
    mode: z.enum(["auto", "suggest"]).default("suggest"),
})
    .default({});
// ── Timeouts ─────────────────────────────────────────────────────────────────
export const TimeoutsSchema = z
    .object({
    /** Per-step timeout in ms. Auto-wait polls until this deadline. Default 15 000ms. */
    step: z.number().positive().default(15_000),
    /** Suite-level wall-clock timeout in ms. Undefined = no ceiling. */
    suite: z.number().positive().optional(),
})
    .default({});
// ── Config ───────────────────────────────────────────────────────────────────
export const ConfigSchema = z.object({
    /** Which platforms this test targets. At least one required. */
    targets: z.array(TargetSchema).min(1),
    app: AppConfigSchema.optional(),
    /**
     * String key-value pairs. Values may contain ${env:KEY} or ${vault:KEY}.
     * Resolved by interpolate() at run time.
     */
    env: z.record(z.string()).default({}),
    /** Named data sources referenced by forEachRow steps. */
    dataSources: z.record(DataSourceSchema).default({}),
    interruptionPolicy: InterruptionPolicySchema,
    healing: HealingPolicySchema,
    timeouts: TimeoutsSchema,
});
// ── Meta ─────────────────────────────────────────────────────────────────────
export const MetaSchema = z.object({
    /** ULID — time-sortable, stable across file renames. */
    id: z.string().min(1),
    name: z.string().min(1),
    tags: z.array(z.string()).default([]),
    owner: z.string().optional(),
    createdBy: z.string().optional(),
});
// ── Flow ─────────────────────────────────────────────────────────────────────
export const FlowSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    /**
     * If true, the runner starts execution from this flow.
     * Exactly one flow must have entry: true in a valid TestIR.
     */
    entry: z.boolean().optional(),
    steps: z.array(IRStepSchema).min(1),
});
// ── Hooks ────────────────────────────────────────────────────────────────────
export const HooksSchema = z
    .object({
    beforeEach: z.array(IRStepSchema).default([]),
    afterEach: z.array(IRStepSchema).default([]),
})
    .default({});
// ── TestIR (top-level) ───────────────────────────────────────────────────────
export const TestIRSchema = z
    .object({
    irVersion: z.literal("1.0"),
    meta: MetaSchema,
    config: ConfigSchema,
    flows: z.array(FlowSchema).min(1),
    hooks: HooksSchema,
})
    .refine((ir) => ir.flows.some((f) => f.entry === true), {
    message: "At least one flow must have entry: true",
    path: ["flows"],
});
//# sourceMappingURL=test-ir.js.map